import React from "react";
import "./PackingListDocumentView.css";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import EmailActivityPanel from "../EmailActivityPanel";
import LifecycleStatusBadge from "./LifecycleStatusBadge";
import LifecycleStatusBanner from "./LifecycleStatusBanner";
import DocumentLifecycleActions from "./DocumentLifecycleActions";
import DocumentVersionTimeline from "./DocumentVersionTimeline";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL"); } catch { return iso; }
}
function fmtEur(n) {
  const v = Number(n || 0);
  return v.toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
}

function joinAddr(...parts) {
  return parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");
}

function Field({ label, children, testid }) {
  return (
    <div className="pkl-field" data-testid={testid}>
      <div className="pkl-field__label">{label}</div>
      <div className="pkl-field__value">{children || "—"}</div>
    </div>
  );
}

export default function PackingListDocumentView({ pkl, onLifecycleChanged }) {
  if (!pkl) return null;

  const lifecycleStatus = pkl.lifecycle_status || null;
  const showHistoricalBanner = ["vervangen", "duplicaat", "geannuleerd"].includes(lifecycleStatus);

  const senderName =
    [pkl.first_name, pkl.last_name].filter(Boolean).join(" ").trim() || "—";
  const senderAddr = joinAddr(
    pkl.sender_address,
    [pkl.sender_postcode, pkl.sender_city].filter(Boolean).join(" "),
    pkl.sender_country,
  );
  const recvAddr = joinAddr(
    pkl.recipient_address,
    pkl.recipient_city,
    pkl.recipient_country,
  );

  const items = Array.isArray(pkl.items) ? pkl.items : [];
  const totalItems = items.reduce((acc, it) => acc + Number(it.quantity || 0), 0);
  // P0 Bugfix 1 Mrt 2026 — totaalwaarde moet aantal * waarde-per-stuk zijn,
  // niet alleen sum(value). Backend persist is al goed (admin_phase10.py).
  const totalValue = items.reduce(
    (acc, it) => acc + (Number(it.value || 0) * Number(it.quantity || 0)),
    0,
  );
  const pidShort = (pkl.id || "").slice(0, 8).toUpperCase() || "—";
  const dgOk = !!pkl.dangerous_goods_confirmed;

  return (
    <article className="pkl-doc" data-testid="pkl-doc">
      {/* Fase 2B — Lifecycle status + acties + waarschuwing */}
      {lifecycleStatus && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <LifecycleStatusBadge status={lifecycleStatus} />
            {pkl.version_number && (
              <span className="text-xs text-gray-600">
                Versie {pkl.version_number}
              </span>
            )}
          </div>
          <DocumentLifecycleActions
            docType="packing_list"
            docId={pkl.id}
            lifecycleStatus={lifecycleStatus}
            onChanged={onLifecycleChanged}
          />
        </div>
      )}
      {showHistoricalBanner && (
        <LifecycleStatusBanner
          status={lifecycleStatus}
          reason={pkl.lifecycle_reason}
          replacedBy={pkl.superseded_by_document_id}
          replacesId={pkl.replaces_document_id}
        />
      )}

      {/* Header */}
      <header className="pkl-doc__header">
        <h1 className="pkl-doc__title">PAKLIJST</h1>
        <p className="pkl-doc__brand">
          5999Cargo Group Europe — Packing list / shipment declaration
        </p>
      </header>

      {/* Meta ribbon */}
      <div className="pkl-doc__meta">
        <div className="pkl-doc__meta-cell">
          <div className="label">Paklijstnr.</div>
          <div className="val" data-testid="pkl-doc-id">{pidShort}</div>
        </div>
        <div className="pkl-doc__meta-cell">
          <div className="label">Datum</div>
          <div className="val">{fmtDate(pkl.created_at)}</div>
        </div>
        <div className="pkl-doc__meta-cell">
          <div className="label">Bestemming</div>
          <div className="val">{pkl.destination || "—"}</div>
        </div>
        <div className="pkl-doc__meta-cell">
          <div className="label">Totale waarde</div>
          <div className="val" data-testid="pkl-doc-total-value">
            {fmtEur(pkl.total_value || totalValue)}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="pkl-doc__body">
        {/* Sender + Receiver */}
        <div className="pkl-grid-2">
          <section className="pkl-card" data-testid="pkl-doc-sender">
            <h3 className="pkl-card__title">Verzender</h3>
            <div className="pkl-card__body">
              <Field label="Naam">{senderName}</Field>
              {pkl.company_name && <Field label="Bedrijf">{pkl.company_name}</Field>}
              <Field label="E-mail">{pkl.email}</Field>
              <Field label="Telefoon">{pkl.phone}</Field>
              <Field label="Adres">{senderAddr}</Field>
            </div>
          </section>

          <section className="pkl-card" data-testid="pkl-doc-receiver">
            <h3 className="pkl-card__title">Ontvanger</h3>
            <div className="pkl-card__body">
              <Field label="Naam">{pkl.recipient_name}</Field>
              {pkl.recipient_company && <Field label="Bedrijf">{pkl.recipient_company}</Field>}
              {pkl.recipient_email && <Field label="E-mail">{pkl.recipient_email}</Field>}
              <Field label="Telefoon">{pkl.recipient_phone}</Field>
              <Field label="Adres">{recvAddr}</Field>
              {pkl.recipient_instructions && (
                <Field label="Instructies">
                  <div style={{ whiteSpace: "pre-wrap" }}>{pkl.recipient_instructions}</div>
                </Field>
              )}
            </div>
          </section>
        </div>

        {/* Shipment */}
        <section className="pkl-card" data-testid="pkl-doc-shipment">
          <h3 className="pkl-card__title">Zendinggegevens</h3>
          <div className="pkl-card__body">
            <div className="pkl-grid-2">
              <div>
                <Field label="Bestemming">{pkl.destination}</Field>
                <Field label="Methode">{pkl.method}</Field>
              </div>
              <div>
                <Field label="Aantal items">{pkl.item_count ?? totalItems}</Field>
                <Field label="W-nummer">
                  <span style={{ fontFamily: "monospace" }}>{pkl.w_number || "—"}</span>
                </Field>
              </div>
            </div>

            {/* Dangerous goods badge */}
            <div style={{ marginTop: 14 }}>
              {dgOk ? (
                <span className="pkl-badge pkl-badge--ok" data-testid="pkl-doc-dg-ok">
                  <ShieldCheck className="w-4 h-4" />
                  Bevestigd — geen verboden goederen / Dangerous Goods opgegeven
                </span>
              ) : (
                <span className="pkl-badge pkl-badge--warn" data-testid="pkl-doc-dg-warn">
                  <ShieldAlert className="w-4 h-4" />
                  Niet bevestigd — afzender heeft de Dangerous-Goods verklaring NIET afgegeven
                </span>
              )}
              {pkl.dangerous_goods_confirmed_at && (
                <div style={{ fontSize: 12, color: "#514e4e", marginTop: 4 }}>
                  Bevestigd op: {fmtDateTime(pkl.dangerous_goods_confirmed_at)}
                </div>
              )}
            </div>

            {pkl.note && (
              <div style={{ marginTop: 14 }}>
                <Field label="Opmerking">
                  <div style={{ whiteSpace: "pre-wrap" }}>{pkl.note}</div>
                </Field>
              </div>
            )}
          </div>
        </section>

        {/* Items */}
        <section className="pkl-card" data-testid="pkl-doc-items">
          <h3 className="pkl-card__title">Inhoud / Items</h3>
          <div style={{ overflowX: "auto" }}>
            <table className="pkl-table">
              <thead>
                <tr>
                  <th className="box">Artikel</th>
                  <th>Omschrijving</th>
                  <th className="num">Aantal</th>
                  <th className="cond">Staat</th>
                  <th className="money">Waarde per stuk</th>
                  <th className="money">Regelwaarde</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: "center", color: "#94a3b8", padding: 24 }}>Geen items opgegeven.</td></tr>
                ) : items.map((it, i) => {
                  const qty = Number(it.quantity || 0);
                  const unit = Number(it.value || 0);
                  const line = qty * unit;
                  return (
                    <tr key={i} data-testid={`pkl-doc-item-${i}`}>
                      <td className="box">{it.box_number || "—"}</td>
                      <td>
                        {it.description}
                        {it.note && (
                          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                            {it.note}
                          </div>
                        )}
                      </td>
                      <td className="num">{qty}</td>
                      <td className="cond">{it.condition || "—"}</td>
                      <td className="money">{fmtEur(unit)}</td>
                      <td className="money" data-testid={`pkl-doc-item-${i}-line-value`}>{fmtEur(line)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>Totaal items: {totalItems}</td>
                  <td className="money" style={{ textAlign: "right" }}>Totale waarde:</td>
                  <td className="money" data-testid="pkl-doc-table-total">
                    {fmtEur(pkl.total_value || totalValue)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="pkl-doc__disclaimer" data-testid="pkl-doc-disclaimer">
          <div className="heading">Verklaring van de afzender</div>
          <p style={{ margin: 0 }}>
            De afzender verklaart dat deze paklijst naar waarheid is ingevuld en dat de
            zending geen verboden goederen of gevaarlijke stoffen bevat die zonder
            voorafgaande toestemming niet vervoerd mogen worden.
          </p>
          <p style={{ margin: "8px 0 0" }}>
            5999Cargo Group Europe is niet verantwoordelijk voor onjuist opgegeven inhoud,
            waarde of ontbrekende documenten.
          </p>
        </div>
      </div>
      {pkl?.id && (
        <EmailActivityPanel refKind="packing_list" refId={pkl.id} />
      )}
      {pkl?.id && lifecycleStatus && (
        <section className="mt-4 border border-gray-200 p-3 bg-white">
          <h3 className="text-sm font-chivo font-bold text-[#0A192F] mb-2">
            Versiehistorie
          </h3>
          <DocumentVersionTimeline docType="packing_list" docId={pkl.id} />
        </section>
      )}
    </article>
  );
}
